/* ------------------------------------------------------------------
   AIBulkCreatePage.js — AI Studio → AI Bulk Post Creation.
   Guided wizard: Goal → Source → Options → Generate → Review (→ Schedule).
   Generated posts persist as drafts (ai_bulk_posts) so the review survives
   navigation; the Schedule step is added in Phase 4.
------------------------------------------------------------------ */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Magic, Trash, Image as ImageIcon, CheckCircleFill, ArrowRepeat } from 'react-bootstrap-icons';
import AccountSelector from '../post/AccountSelector';
import LimitReachedModal from '../../../components/LimitReachedModal';
import PrefillPill from './PrefillPill';
import { listingSite, isBlockingSite, blockedPullMessage } from './listingLinks';

const ax = axios.create({ withCredentials: true });
const inferDefaultFormat = () => 'feed';
const userTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; } })();
// "Describe your AI photos" (T40): words only, up to 200 characters — the
// same rule as the server (backend/services/imageBrief.js).
const BRIEF_MAX = 200;
const BRIEF_LINK_RE = /(?:[a-z][a-z0-9+.-]*:\/\/|www\.)\S+|\b[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:com|net|org|io|co|ly|me|app|shop|store|biz|info|us|uk|ca|au)\b/i;

const STEPS = [
  { key: 'goal',     label: 'Goal' },
  { key: 'source',   label: 'Details' },
  { key: 'options',  label: 'Options' },
  { key: 'review',   label: 'Review' },
  { key: 'schedule', label: 'Schedule' },
];

export default function AIBulkCreatePage({ prefill = null }) {
  const { jobId } = useParams();
  const navigate = useNavigate();

  // `prefill` (after a sign-up from the name generators, T24): open on the
  // Details step of that goal with its fields filled in.
  const [step, setStep] = useState(prefill ? 'source' : 'goal');
  const [goals, setGoals] = useState([]);
  const [userId, setUserId] = useState(null);
  const [msg, setMsg] = useState(null);
  const flash = (text, ok = false) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 5000); };

  // wizard state
  const [goal, setGoal] = useState(prefill ? prefill.goal : '');
  const [source, setSource] = useState(() => (prefill ? { ...prefill.fields } : {}));   // {title,description,...goalFields}
  const [opts, setOpts] = useState({ count: 8, tone: 'Friendly', language: 'English', emojis: true, hashtags: { enabled: true, count: 3 }, cta: 'soft' });
  const [imageStrategy, setImageStrategy] = useState('ai');   // AI-generated photos selected by default
  const [postFormat, setPostFormat] = useState('single');     // 'single' | 'carousel'
  const [slidesPerPost, setSlidesPerPost] = useState(2);      // AI carousel: images generated per post (each = 1 bulk credit)
  const [stockKeywords, setStockKeywords] = useState('');
  const [uploads, setUploads] = useState([]);               // S3 urls
  const [siteImages, setSiteImages] = useState([]);         // images pulled from the URL
  const [pullStatus, setPullStatus] = useState(null);       // {done, images, gotText} — last URL pull outcome
  const [aiStyle] = useState('');                           // optional style prompt for AI images (UI removed; default)
  const [imageBrief, setImageBrief] = useState('');         // optional: what the AI photos should show (T40)
  const [referenceImageUrls, setReferenceImageUrls] = useState([]);  // references for image-to-image (multi)
  const seenRef = useRef(new Set());                        // candidates already auto-selected once
  const strategyTouched = useRef(false);                    // did the user manually pick an image strategy?

  const [generating, setGenerating] = useState(false);
  const [posts, setPosts] = useState([]);
  const [activeJob, setActiveJob] = useState(null);

  // schedule step
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [strategy, setStrategy] = useState('spread');
  const [perDay, setPerDay] = useState(3);
  const [startDate, setStartDate] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [done, setDone] = useState(null);    // {scheduled} result

  // bootstrap: goals + userId + AI-photo quota
  const [imageQuota, setImageQuota] = useState(null);   // {quota, used, remaining}
  useEffect(() => {
    (async () => {
      try { const { data } = await ax.get('/api/ai/bulk/goals'); setGoals(data.goals || []); } catch { /* ignore */ }
      try { const me = await ax.get('/api/auth/me'); if (me.data?.user?.id) setUserId(me.data.user.id); } catch { /* ignore */ }
      try { const { data } = await ax.get('/api/ai/bulk/image-quota'); setImageQuota(data); } catch { /* ignore */ }
    })();
  }, []);
  const imgLeft = imageQuota ? imageQuota.remaining : null;
  const [showUpgrade, setShowUpgrade] = useState(false);

  // resume an existing job → jump to review
  const loadJob = useCallback(async (id) => {
    try {
      const { data } = await ax.get(`/api/ai/bulk/jobs/${id}`);
      setActiveJob(data.job); setPosts(data.posts || []); setGoal(data.job.goal); setStep('review');
    } catch { flash('Could not load that batch.'); navigate('/dashboard/studio/bulk'); }
  }, [navigate]);
  useEffect(() => { if (jobId) loadJob(jobId); }, [jobId, loadJob]);

  // auto-select every newly-available image as a reference (user can toggle off)
  useEffect(() => {
    const candidates = [...siteImages, ...uploads];
    const fresh = candidates.filter(u => !seenRef.current.has(u));
    if (fresh.length) {
      fresh.forEach(u => seenRef.current.add(u));
      setReferenceImageUrls(prev => [...new Set([...prev, ...fresh])]);
      // Goal-aware default when photos become available:
      //  • listing → the photos ARE the post (real property), so use them directly ("My photos")
      //  • everything else → keep AI-generated default; the photos feed it as references,
      //    so users don't silently lose AI photo generation by uploading a reference.
      if (!strategyTouched.current && goal === 'listing') setImageStrategy('upload');
    }
  }, [siteImages, uploads, goal]);
  const toggleRef = (u) => setReferenceImageUrls(prev => prev.includes(u) ? prev.filter(x => x !== u) : [...prev, u]);

  // AI carousel = K AI renders per post, billed 1 credit per slide.
  const aiCarousel = imageStrategy === 'ai' && postFormat === 'carousel';
  const slides = aiCarousel ? Math.max(2, Math.min(4, slidesPerPost)) : 1;
  const briefText = imageBrief.replace(/\s+/g, ' ').trim();
  const briefHasLink = BRIEF_LINK_RE.test(briefText);
  // max posts allowed given plan/format (AI carousel: ≤8 total renders, and by remaining credits/slide)
  const maxPosts = (() => {
    if (imageStrategy !== 'ai') return imgLeft == null ? 15 : Math.min(15, imgLeft);
    if (aiCarousel) { const byTotal = Math.floor(8 / slides); const byCredits = imgLeft == null ? byTotal : Math.floor(imgLeft / slides); return Math.max(1, Math.min(byTotal, byCredits)); }
    return imgLeft == null ? 6 : Math.min(6, imgLeft);
  })();

  // keep the post count within the current cap
  useEffect(() => {
    setOpts(o => o.count > maxPosts ? { ...o, count: Math.max(1, maxPosts) } : o);
  }, [maxPosts]);

  const goalDef = goals.find(g => g.key === goal);
  // A prefilled goal this server doesn't offer: start from the goal list instead.
  useEffect(() => {
    if (step === 'source' && goals.length && !goalDef) { setGoal(''); setSource({}); setStep('goal'); }
  }, [step, goals, goalDef]);

  /* ── generate ── */
  async function generate() {
    setGenerating(true); setStep('review'); setPosts([]);
    try {
      const { data } = await ax.post('/api/ai/bulk/generate', {
        goal, count: opts.count, source,
        tone: opts.tone, language: opts.language, emojis: opts.emojis, hashtags: opts.hashtags, cta: opts.cta,
        imageStrategy, stockKeywords, postFormat, slidesPerPost: slides,
        uploadedImageUrls: referenceImageUrls,   // "My photos": use the selected pulled/uploaded photos directly
        aiStyle, referenceImageUrls,             // "AI photos": same selected photos act as references
        ...(imageStrategy === 'ai' && briefText && !briefHasLink ? { imageBrief: briefText.slice(0, BRIEF_MAX) } : {}),
      });
      setPosts(data.posts || []);
      setActiveJob({ id: data.jobId, goal });
      if (data.imageBalance) setImageQuota(data.imageBalance);
      if (data.jobId) navigate(`/dashboard/studio/bulk/${data.jobId}`, { replace: true });
      if (data.imageError) flash(data.imageError, false);
    } catch (e) {
      if (e.response?.data?.error === 'image_limit' || e.response?.status === 402) setShowUpgrade(true);
      else flash(e.response?.data?.error || 'Generation failed.');
      setStep('options');
    } finally { setGenerating(false); }
  }

  /* ── review actions ── */
  async function saveCaption(p, caption) {
    try { await ax.patch(`/api/ai/bulk/posts/${p.id}`, { caption }); setPosts(ps => ps.map(x => x.id === p.id ? { ...x, caption } : x)); }
    catch { flash('Could not save caption.'); }
  }
  async function discard(p) {
    try { await ax.post(`/api/ai/bulk/posts/${p.id}/discard`); setPosts(ps => ps.filter(x => x.id !== p.id)); }
    catch { flash('Could not remove that post.'); }
  }
  async function reorderSlides(p, urls) {
    setPosts(ps => ps.map(x => x.id === p.id ? { ...x, media_urls: urls, media_url: urls[0] } : x));  // optimistic
    try { await ax.patch(`/api/ai/bulk/posts/${p.id}`, { mediaUrls: urls }); }
    catch { flash('Could not save the new photo order.'); }
  }
  async function regenCaption(p) {
    try { const { data } = await ax.post(`/api/ai/bulk/posts/${p.id}/regenerate-caption`); setPosts(ps => ps.map(x => x.id === p.id ? { ...x, caption: data.caption } : x)); return data.caption; }
    catch (e) { flash(e.response?.status === 402 ? 'Out of AI credits.' : 'Could not rewrite the caption.'); return null; }
  }
  async function regenImage(p) {
    try {
      const { data } = await ax.post(`/api/ai/bulk/posts/${p.id}/regenerate-image`);
      setPosts(ps => ps.map(x => x.id === p.id ? { ...x, media_url: data.media_url, media_source: 'ai' } : x));
      ax.get('/api/ai/bulk/image-quota').then(({ data }) => setImageQuota(data)).catch(() => {});
      return true;
    } catch (e) {
      const limit = e.response?.data?.error === 'image_limit' || e.response?.status === 402;
      if (limit) setShowUpgrade(true);
      else flash(e.response?.data?.error || 'Could not regenerate the image.');
      return false;
    }
  }

  /* ── schedule ── */
  async function schedule() {
    const postIds = posts.filter(p => !p.error).map(p => p.id);
    if (!postIds.length) return flash('No posts to schedule.');
    if (!selectedAccounts.length) return flash('Pick at least one account.');
    const accounts = selectedAccounts.map(a => ({
      id: a.externalId || a.id, platform: a.platform,
      username: a.displayName || '', profileImage: a.userAvatar || '',
    }));
    setScheduling(true);
    try {
      const { data } = await ax.post('/api/ai/bulk/schedule', { postIds, accounts, strategy, startDate: startDate || undefined, perDay, userTz });
      setDone(data);
    } catch (e) {
      if (e.response?.status === 402) flash(e.response.data?.message || 'Not enough post quota for this batch.');
      else flash(e.response?.data?.error || 'Scheduling failed.');
    } finally { setScheduling(false); }
  }

  /* ── uploads ── */
  async function uploadOne(file) {
    const fd = new FormData(); fd.append('files', file); fd.append('userId', userId || ''); fd.append('prefix', 'ai-bulk');
    const { data } = await ax.post('/api/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data?.files?.[0]?.url || null;
  }
  async function onUpload(e) {
    const files = [...(e.target.files || [])]; e.target.value = '';
    if (!files.length) return;
    for (const file of files) {
      try { const url = await uploadOne(file); if (url) setUploads(u => [...u, url]); }
      catch { flash('An image failed to upload.'); }
    }
  }
  // Upload from the AI reference picker → it auto-selects via the effect above.
  async function onReferenceUpload(e) {
    const files = [...(e.target.files || [])]; e.target.value = '';
    for (const file of files) {
      try { const url = await uploadOne(file); if (url) setUploads(u => [...u, url]); }
      catch { flash('Image failed to upload.'); }
    }
  }

  const stepIdx = STEPS.findIndex(s => s.key === (step === 'review' ? 'review' : step));
  const canSource = !!goal;
  const requiredFilled = goalDef ? goalDef.fields.filter(f => !f.optional).every(f => (source[f.key] || '').trim()) : false;

  return (
    <div style={{ maxWidth: 920, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>AI Bulk Post Creation</h1>
        <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>Answer a few questions and generate a batch of on-brand posts to review and schedule.</p>
      </div>

      {msg && <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, fontSize: 14, fontWeight: 600,
        background: msg.ok ? '#ecfdf5' : '#fef2f2', color: msg.ok ? '#059669' : '#dc2626', border: `1px solid ${msg.ok ? '#a7f3d0' : '#fecaca'}` }}>{msg.text}</div>}

      {/* stepper */}
      <Stepper steps={STEPS} currentIdx={stepIdx} />

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 22, marginTop: 16 }}>
        {/* STEP 1 — GOAL */}
        {step === 'goal' && (
          <>
            <SectionTitle>What do you want to post about?</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
              {goals.map(g => (
                <button key={g.key} onClick={() => { setGoal(g.key); setSource(prefill && g.key === prefill.goal ? { ...prefill.fields } : {}); }}
                  style={{ textAlign: 'left', padding: 16, borderRadius: 12, cursor: 'pointer',
                    border: `2px solid ${goal === g.key ? '#4f46e5' : '#e5e7eb'}`, background: goal === g.key ? '#eef2ff' : '#fff' }}>
                  <div style={{ fontWeight: 700, color: '#111827', marginBottom: 4 }}>{g.label}</div>
                  <div style={{ fontSize: 12.5, color: '#6b7280' }}>{g.fields.length} quick fields</div>
                </button>
              ))}
            </div>
            <Footer right={<button disabled={!canSource} onClick={() => setStep('source')} style={primary(!canSource)}>Next</button>} />
          </>
        )}

        {/* STEP 2 — SOURCE */}
        {step === 'source' && goalDef && (
          <>
            {prefill && goal === prefill.goal ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Tell us about it</div>
                <PrefillPill prefill={prefill} goalLabel={goalDef.label} />
              </div>
            ) : <SectionTitle>Tell us about it</SectionTitle>}
            <Label>Website or page link (optional — we’ll try to pull details + images)</Label>
            {goal === 'listing' && (
              <div style={{
                display: 'flex', gap: 8, alignItems: 'flex-start',
                background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
                padding: '8px 10px', margin: '6px 0 4px', fontSize: 12, color: '#1e40af',
              }}>
                <i className="fas fa-lightbulb" style={{ marginTop: 2, color: '#2563eb' }} />
                <div data-testid="listing-photo-choices">
                  <strong>Add your listing photos — pick one:</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    <li><strong>Paste a Redfin link</strong> — we pull the photos and details for you.</li>
                    <li><strong>Upload my listing photos</strong> — add them in the next step for full-quality images.</li>
                    <li><strong>Paste a Zillow or Realtor.com link</strong> — we’ll try once, but these sites usually block photo pulls.</li>
                  </ul>
                </div>
              </div>
            )}
            <UrlPull goal={goal} onFill={(d) => {
              setSource(s => {
                const next = { ...s, title: d.title || s.title, description: d.description || s.description };
                if (d.suggested) for (const [k, v] of Object.entries(d.suggested)) { if (v && !String(next[k] || '').trim()) next[k] = v; }
                return next;
              });
              if (d.images?.length) setSiteImages(d.images);
              const gotText = !!(d.title || d.description || (d.suggested && Object.keys(d.suggested).length));
              setPullStatus({ done: true, images: d.images?.length || 0, gotText, notice: d.notice || '' });
            }} flash={flash} />
            {siteImages.length > 0 && (
              <p style={{ fontSize: 12, color: '#16a34a', marginTop: 6 }}>Found {siteImages.length} image{siteImages.length === 1 ? '' : 's'} on your site — you can use one as a reference for AI photos in the next step.</p>
            )}
            {pullStatus?.done && pullStatus.images === 0 && (
              <p style={{ fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px', marginTop: 6 }}>
                <i className="fas fa-image" style={{ marginRight: 6 }} />
                {pullStatus.notice
                  ? <>{pullStatus.notice}{pullStatus.gotText ? ' We filled in the details we could read.' : ''} Or choose <strong>AI-generated photos</strong> in the next step.</>
                  : <>{pullStatus.gotText
                    ? 'We pulled the details but couldn’t get photos from this page — some sites load their images in a way we can’t read automatically.'
                    : 'We couldn’t pull from this page (the site blocked the request).'}{' '}
                    Upload your own photos below, or choose <strong>AI-generated photos</strong> in the next step.</>}
              </p>
            )}
            {goalDef.fields.map(f => (
              <div key={f.key} style={{ marginTop: 12 }}>
                <Label>{f.label}{!f.optional && <span style={{ color: '#dc2626' }}> *</span>}</Label>
                <input value={source[f.key] || ''} onChange={e => setSource(s => ({ ...s, [f.key]: e.target.value }))} style={inputStyle} />
              </div>
            ))}
            <div style={{ marginTop: 12 }}>
              <Label>Anything else to mention? (optional)</Label>
              <textarea value={source.extra || ''} onChange={e => setSource(s => ({ ...s, extra: e.target.value }))} style={{ ...inputStyle, minHeight: 70, resize: 'vertical' }} />
            </div>
            <Footer
              left={<button onClick={() => setStep('goal')} style={ghost}>Back</button>}
              right={<button disabled={!requiredFilled} onClick={() => setStep('options')} style={primary(!requiredFilled)}>Next</button>} />
          </>
        )}

        {/* STEP 3 — OPTIONS */}
        {step === 'options' && (
          <>
            <SectionTitle>How should we generate them?</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 16 }}>
              <div>
                <Label>How many posts? ({opts.count})</Label>
                <input type='range' min={1} max={Math.max(1, maxPosts)} value={opts.count} onChange={e => setOpts(o => ({ ...o, count: Number(e.target.value) }))} style={{ width: '100%' }} />
              </div>
              <div>
                <Label>Language</Label>
                <input value={opts.language} onChange={e => setOpts(o => ({ ...o, language: e.target.value }))} style={inputStyle} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 20, marginTop: 14, flexWrap: 'wrap' }}>
              <label style={checkRow}><input type='checkbox' checked={opts.emojis} onChange={e => setOpts(o => ({ ...o, emojis: e.target.checked }))} /> Use emojis</label>
              <label style={checkRow}><input type='checkbox' checked={opts.hashtags.enabled} onChange={e => setOpts(o => ({ ...o, hashtags: { ...o.hashtags, enabled: e.target.checked } }))} /> Add hashtags</label>
              {opts.hashtags.enabled && (
                <span style={{ fontSize: 13.5, color: '#374151' }}>Count:&nbsp;
                  <input type='number' min={1} max={10} value={opts.hashtags.count} onChange={e => setOpts(o => ({ ...o, hashtags: { ...o.hashtags, count: Number(e.target.value) } }))} style={{ ...inputStyle, width: 64, padding: '5px 8px', display: 'inline-block' }} />
                </span>
              )}
            </div>

            <div style={{ height: 1, background: '#f1f5f9', margin: '18px 0' }} />
            <SectionTitle>Images</SectionTitle>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              {[['upload', 'My photos'], ['ai', '✨ AI-generated photos']].map(([v, l]) => (
                <button key={v} onClick={() => { strategyTouched.current = true; setImageStrategy(v); }} style={pill(imageStrategy === v)}>{l}</button>
              ))}
            </div>
            <p style={{ fontSize: 12.5, color: '#6b7280', margin: '0 0 10px' }}>
              {imageStrategy === 'ai'
                ? <>✨ We&rsquo;ll generate a fresh photo for each post{referenceImageUrls.length ? ' from your selected photos' : ''}.{goal === 'listing' && ' For listings, “My photos” usually shows the real property best.'}</>
                : <>Your selected photos are posted as-is, rotated across posts.{goal !== 'listing' && ' Want polished, on-brand images instead? Pick “✨ AI-generated photos”.'}</>}
            </p>

            {/* What the AI photos should show (T40) — the same question the chat asks */}
            {imageStrategy === 'ai' && (
              <div style={{ marginBottom: 12 }}>
                <Label>Describe your AI photos (optional)</Label>
                <input value={imageBrief} onChange={e => setImageBrief(e.target.value)} maxLength={BRIEF_MAX}
                  aria-label='Describe your AI photos'
                  placeholder='e.g. cozy candles on a wooden table with autumn leaves' style={inputStyle} />
                <p style={{ fontSize: 12, color: briefHasLink ? '#b45309' : '#6b7280', margin: '6px 0 0' }}>
                  {briefHasLink
                    ? 'Describe the photos in words — links can’t be used here.'
                    : 'Every photo will show this, each from a different angle. Leave it empty and we’ll choose.'}
                </p>
              </div>
            )}

            {/* Post type — carousels (multiple images per post) for My photos OR AI */}
            {(imageStrategy === 'upload' || imageStrategy === 'ai') && (
              <div style={{ marginBottom: 12 }}>
                <Label>Post type</Label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                  {[['single', 'Single image'], ['carousel', '◫ Carousel']].map(([v, l]) => (
                    <button key={v} onClick={() => setPostFormat(v)} style={pill(postFormat === v)}>{l}</button>
                  ))}
                </div>

                {/* AI carousel: pick images-per-post; billed per slide */}
                {postFormat === 'carousel' && imageStrategy === 'ai' && (
                  <div style={{ marginTop: 10 }}>
                    <Label>Images per post</Label>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      {[2, 3, 4].map(n => (
                        <button key={n} onClick={() => setSlidesPerPost(n)} style={{ ...pill(slides === n), minWidth: 44 }}>{n}</button>
                      ))}
                    </div>
                  </div>
                )}

                <p style={{ fontSize: 12, color: postFormat === 'carousel' && imageStrategy === 'upload' && referenceImageUrls.length < 2 ? '#b45309' : '#6b7280', margin: '8px 0 0' }}>
                  {postFormat !== 'carousel'
                    ? 'One image per post.'
                    : imageStrategy === 'ai'
                      ? <><strong style={{ color: '#111827' }}>{opts.count} post{opts.count === 1 ? '' : 's'} × {slides} AI images = {opts.count * slides} bulk post{opts.count * slides === 1 ? '' : 's'}.</strong> Each AI image costs 1 bulk post. Carousels publish on Instagram; other platforms post the first image.</>
                      : (referenceImageUrls.length < 2
                          ? 'Select at least 2 photos — a carousel needs multiple images.'
                          : `Your ${referenceImageUrls.length} selected photos are split into ${opts.count} carousel post${opts.count === 1 ? '' : 's'} (multiple images each). Still 1 bulk post per carousel. Carousels publish on Instagram; other platforms post the first image.`)}
                </p>
              </div>
            )}

            {/* Bulk-post allowance (every generated post counts, any image type) */}
            {imgLeft === 0 ? (
              <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
                <div style={{ fontWeight: 700, color: '#3730a3' }}>You’ve used all {imageQuota?.quota} of your bulk posts this month.</div>
                <Link to='/dashboard/plans' style={{ color: '#4f46e5', fontWeight: 700, fontSize: 14 }}>Upgrade for more →</Link>
              </div>
            ) : (
              <p style={{ fontSize: 12.5, color: '#6b7280', margin: '0 0 10px' }}>
                {imgLeft != null && <><strong style={{ color: '#111827' }}>{imgLeft}</strong> of {imageQuota.quota} bulk posts left this month. </>}
                {imageStrategy === 'ai' && !aiCarousel && 'AI photos are built from your selected photos, capped at 6 per batch.'}
                {aiCarousel && 'Each AI carousel image counts as 1 bulk post.'}
              </p>
            )}

            {/* Shared photo picker — used directly ("My photos") or as references ("AI") */}
            <div>
              <Label>{imageStrategy === 'ai' ? 'Photos to base the AI images on (pick one or more)' : 'Photos to use in your posts (pick one or more)'}</Label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[...siteImages, ...uploads].map((u, i) => {
                  const on = referenceImageUrls.includes(u);
                  return (
                    <button key={i} onClick={() => toggleRef(u)} style={{ ...refThumb, ...(on ? refThumbActive : {}), position: 'relative' }}>
                      <img src={u} alt='' style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
                      {on && <span style={{ position: 'absolute', top: 2, right: 2, background: '#4f46e5', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</span>}
                    </button>
                  );
                })}
                <label style={{ ...refThumb, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#6b7280' }} title='Upload photos'>
                  <ImageIcon size={16} />
                  <input type='file' accept='image/*' multiple onChange={onReferenceUpload} style={{ display: 'none' }} />
                </label>
              </div>
              <p style={{ fontSize: 12, color: referenceImageUrls.length ? '#16a34a' : '#9ca3af', marginTop: 6, fontWeight: referenceImageUrls.length ? 600 : 400 }}>
                {referenceImageUrls.length
                  ? (imageStrategy === 'ai'
                      ? `✓ Using ${referenceImageUrls.length} photo${referenceImageUrls.length === 1 ? '' : 's'} as the base for your AI images.`
                      : `✓ Using ${referenceImageUrls.length} photo${referenceImageUrls.length === 1 ? '' : 's'}, rotated across your posts.`)
                  : (imageStrategy === 'ai'
                      ? 'Optional — add photos so the AI matches your product.'
                      : 'Select or upload at least one photo to use in your posts.')}
              </p>
            </div>

            <Footer
              left={<button onClick={() => setStep('source')} style={ghost}>Back</button>}
              right={(() => {
                const blocked = imageStrategy === 'upload' && !referenceImageUrls.length;
                const onClick = () => { if (imgLeft === 0) { setShowUpgrade(true); return; } generate(); };
                return <button onClick={onClick} disabled={blocked} style={primary(blocked)}>
                  <Magic size={15} style={{ marginRight: 6, verticalAlign: '-2px' }} />Generate {opts.count} post{opts.count === 1 ? '' : 's'}</button>;
              })()} />
          </>
        )}

        {/* STEP 4/5 — GENERATING + REVIEW */}
        {step === 'review' && (
          generating ? (
            <div style={{ padding: '56px 0', textAlign: 'center', color: '#6b7280' }}>
              <div className='spinner-border text-primary' role='status' style={{ width: 38, height: 38 }} />
              <div style={{ marginTop: 14, fontWeight: 600, color: '#111827' }}>{imageStrategy === 'ai' ? 'Writing captions and generating images…' : 'Writing your posts…'}</div>
              <div style={{ fontSize: 13 }}>{imageStrategy === 'ai' ? 'AI photos take a bit longer — up to a couple of minutes.' : 'This can take up to a minute for larger batches.'}</div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                <SectionTitle>Review your {posts.length} post{posts.length === 1 ? '' : 's'}</SectionTitle>
                <button onClick={() => { setStep('goal'); setPosts([]); setActiveJob(null); navigate('/dashboard/studio/bulk'); }} style={ghost}>Start over</button>
              </div>
              {posts.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No posts. Try generating again.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 14 }}>
                  {posts.map(p => <ReviewCard key={p.id} post={p} onSave={saveCaption} onDiscard={discard} onRegenCaption={regenCaption} onRegenImage={regenImage} onReorder={reorderSlides} />)}
                </div>
              )}
              <Footer right={
                <button disabled={!posts.filter(p => !p.error).length} onClick={() => setStep('schedule')} style={primary(!posts.filter(p => !p.error).length)}>
                  Continue to schedule →
                </button>} />
              <p style={{ fontSize: 12, color: '#9ca3af', textAlign: 'right', marginTop: 6 }}>Your drafts are saved automatically.</p>
            </>
          )
        )}

        {/* STEP 6 — SCHEDULE */}
        {step === 'schedule' && (
          done ? (
            <div style={{ padding: '48px 0', textAlign: 'center' }}>
              <CheckCircleFill size={46} color='#16a34a' />
              <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '14px 0 4px' }}>Scheduled {done.scheduled} post{done.scheduled === 1 ? '' : 's'}!</div>
              <p style={{ color: '#6b7280', fontSize: 14 }}>{done.posts} post{done.posts === 1 ? '' : 's'} × {done.accounts} account{done.accounts === 1 ? '' : 's'}. They’ll publish automatically at the chosen times.</p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 18 }}>
                <button onClick={() => navigate('/dashboard/calendar')} style={primary(false)}>View calendar</button>
                <button onClick={() => { setStep('goal'); setPosts([]); setActiveJob(null); setDone(null); setSelectedAccounts([]); navigate('/dashboard/studio/bulk'); }} style={ghost}>Create another batch</button>
              </div>
            </div>
          ) : (
            <>
              <SectionTitle>Where and when should these go out?</SectionTitle>
              <AccountSelector selectedAccounts={selectedAccounts} setSelectedAccounts={setSelectedAccounts} inferDefaultFormat={inferDefaultFormat} />

              <Label>Scheduling</Label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <button onClick={() => setStrategy('spread')} style={pill(strategy === 'spread')}>Spread evenly</button>
                <button onClick={() => setStrategy('best-time')} style={pill(strategy === 'best-time')}>Best time per platform</button>
              </div>
              {strategy === 'spread' && (
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <div><Label>Start date</Label><input type='date' value={startDate} onChange={e => setStartDate(e.target.value)} style={inputStyle} /></div>
                  <div><Label>Posts per day</Label><input type='number' min={1} max={10} value={perDay} onChange={e => setPerDay(Number(e.target.value))} style={{ ...inputStyle, width: 110 }} /></div>
                </div>
              )}
              {strategy === 'best-time' && (
                <p style={{ fontSize: 13, color: '#6b7280' }}>One post per platform per day at that platform’s typical best hour, starting tomorrow.</p>
              )}

              <div style={{ marginTop: 14, padding: '10px 14px', background: '#f8fafc', borderRadius: 8, fontSize: 13.5, color: '#374151' }}>
                Will schedule <strong>{posts.filter(p => !p.error).length * (selectedAccounts.length || 0)}</strong> post{(posts.filter(p => !p.error).length * (selectedAccounts.length || 0)) === 1 ? '' : 's'}
                {' '}({posts.filter(p => !p.error).length} × {selectedAccounts.length || 0} account{selectedAccounts.length === 1 ? '' : 's'}). Counts toward your post quota.
              </div>

              <Footer
                left={<button onClick={() => setStep('review')} style={ghost}>Back</button>}
                right={<button onClick={schedule} disabled={scheduling || !selectedAccounts.length} style={primary(scheduling || !selectedAccounts.length)}>{scheduling ? 'Scheduling…' : 'Schedule posts'}</button>} />
            </>
          )
        )}
      </div>

      <LimitReachedModal
        show={showUpgrade}
        onHide={() => setShowUpgrade(false)}
        title="You’re out of bulk posts"
        message={`You’ve used all ${imageQuota?.quota ?? ''} of your bulk posts for this month. Upgrade your plan to keep creating posts with the bulk creator.`}
      />
    </div>
  );
}

/* ── URL pull sub-component ── */
function UrlPull({ goal, onFill, flash }) {
  const [url, setUrl] = useState(''); const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  // For listings, Redfin reliably serves photos to an automated pull.
  // Zillow and Realtor.com usually block it: we try once and, if nothing
  // comes back, say so in the same words as the chat (T41). Other sites
  // aren't tried for listings.
  async function pull() {
    if (!url.trim()) return;
    setErr('');
    const site = listingSite(url.trim());
    if (goal === 'listing' && !site) {
      setErr('For listings, paste a Redfin, Zillow or Realtor.com link — or upload your listing photos in the next step.');
      return;
    }
    const portal = goal === 'listing' && isBlockingSite(url.trim());
    setBusy(true);
    try {
      const { data } = await ax.post('/api/ai/bulk/url-preview', { url: url.trim(), goal });
      if (portal && data.blocked) onFill({ ...data, notice: data.notice || blockedPullMessage(site) });   // details still fill when there were any
      else if (data.source === 'none') flash('Couldn’t read that page — fill in the details below.', false);
      else {
        onFill(data);
        const filled = data.suggested ? Object.keys(data.suggested).length : 0;
        flash(filled ? `Pulled details and filled ${filled} field${filled === 1 ? '' : 's'} — review them below.` : 'Pulled details from the page.', true);
      }
    } catch (e) {
      if (portal) onFill({ images: [], blocked: true, notice: blockedPullMessage(site) });
      else if (e.response?.status === 422) flash('That link can’t be fetched.');
      else flash('Couldn’t read that page — fill in the details below.');
    } finally { setBusy(false); }
  }
  return (
    <>
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={url} onChange={e => { setUrl(e.target.value); if (err) setErr(''); }}
          placeholder={goal === 'listing' ? 'https://www.redfin.com/CT/Bristol/…' : 'https://yourwebsite.com/page'}
          style={inputStyle} />
        <button onClick={pull} disabled={busy || !url.trim()} style={{ ...ghost, whiteSpace: 'nowrap' }}>{busy ? 'Reading…' : 'Pull'}</button>
      </div>
      {err && <p style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}><i className="fas fa-circle-info" style={{ marginRight: 6 }} />{err}</p>}
    </>
  );
}

/* ── review card ── */
function ReviewCard({ post, onSave, onDiscard, onRegenCaption, onRegenImage, onReorder }) {
  const [caption, setCaption] = useState(post.caption || '');
  const [busyCap, setBusyCap] = useState(false);
  const [busyImg, setBusyImg] = useState(false);
  const [order, setOrder] = useState(() => (Array.isArray(post.media_urls) ? post.media_urls.filter(Boolean) : []));
  const [idx, setIdx] = useState(0);
  const dragFrom = useRef(null);
  const dirty = caption !== (post.caption || '');

  async function rewrite() { setBusyCap(true); try { const c = await onRegenCaption(post); if (c != null) setCaption(c); } finally { setBusyCap(false); } }
  async function reimage() { setBusyImg(true); try { await onRegenImage(post); } finally { setBusyImg(false); } }

  const isCarousel = order.length > 1;
  const go = (d) => setIdx(i => (i + d + order.length) % order.length);
  function move(from, to) {
    if (from == null || to == null || from === to) return;
    const a = [...order]; const [m] = a.splice(from, 1); a.splice(to, 0, m);
    setOrder(a); setIdx(Math.max(0, Math.min(a.length - 1, to))); onReorder && onReorder(post, a);
  }
  const navBtn = (side) => ({ position: 'absolute', top: '50%', [side]: 6, transform: 'translateY(-50%)',
    width: 30, height: 30, borderRadius: '50%', border: 'none', cursor: 'pointer', fontSize: 18, lineHeight: '1',
    background: 'rgba(17,24,39,0.6)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' });

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'relative' }}>
        {isCarousel
          ? <img src={order[idx]} alt='' style={{ width: '100%', aspectRatio: '4 / 5', objectFit: 'cover', display: 'block', background: '#f3f4f6' }} />
          : post.media_url
            ? <img src={post.media_url} alt='' style={{ width: '100%', aspectRatio: '4 / 5', objectFit: 'cover', display: 'block', background: '#f3f4f6' }} />
            : <div style={{ width: '100%', aspectRatio: '4 / 5', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}><ImageIcon size={26} /></div>}
        {isCarousel && (
          <>
            <div style={{ position: 'absolute', top: 8, left: 8, padding: '4px 8px', fontSize: 11, fontWeight: 700, borderRadius: 7, background: 'rgba(17,24,39,0.78)', color: '#fff', backdropFilter: 'blur(2px)' }}>
              ◫ {idx + 1} / {order.length}
            </div>
            <button onClick={() => go(-1)} style={navBtn('left')} title='Previous'>‹</button>
            <button onClick={() => go(1)} style={navBtn('right')} title='Next'>›</button>
          </>
        )}
        {busyImg && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className='spinner-border text-light' role='status' style={{ width: 26, height: 26 }} /></div>}
        {!isCarousel && (
          <button onClick={reimage} disabled={busyImg} title='Generate a new image' style={{
            position: 'absolute', bottom: 8, right: 8, display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '5px 9px', fontSize: 11.5, fontWeight: 600, border: 'none', borderRadius: 7,
            background: 'rgba(17,24,39,0.78)', color: '#fff', cursor: busyImg ? 'default' : 'pointer', backdropFilter: 'blur(2px)',
          }}><ArrowRepeat size={12} /> {busyImg ? 'Generating…' : 'New image'}</button>
        )}
      </div>
      {isCarousel && (
        <div style={{ padding: '6px 8px 0' }}>
          <div style={{ fontSize: 10.5, color: '#9ca3af', marginBottom: 3 }}>Drag to reorder · tap to preview · first photo is the cover</div>
          <div style={{ display: 'flex', gap: 4, overflowX: 'auto', paddingBottom: 2 }}>
            {order.map((u, i) => (
              <img key={u + i} src={u} alt='' draggable
                onDragStart={() => { dragFrom.current = i; }}
                onDragOver={e => e.preventDefault()}
                onDrop={() => { move(dragFrom.current, i); dragFrom.current = null; }}
                onClick={() => setIdx(i)}
                style={{ width: 38, height: 46, objectFit: 'cover', borderRadius: 5, flexShrink: 0, cursor: 'grab',
                  border: i === idx ? '2px solid #4f46e5' : '1px solid #e5e7eb', opacity: i === idx ? 1 : 0.75 }} />
            ))}
          </div>
        </div>
      )}
      <div style={{ padding: 12, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {post.topic && <div style={{ fontSize: 11, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 6 }}>{post.topic}</div>}
        <textarea value={caption} onChange={e => setCaption(e.target.value)} placeholder={post.error ? 'Generation failed — rewrite or remove.' : ''} style={{ ...inputStyle, minHeight: 96, resize: 'vertical', fontSize: 13.5 }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 6 }}>
          <button onClick={() => onDiscard(post)} style={{ ...miniBtn, color: '#dc2626', borderColor: '#fecaca' }}><Trash size={12} /> Remove</button>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={rewrite} disabled={busyCap} style={{ ...miniBtn, opacity: busyCap ? 0.6 : 1 }}><ArrowRepeat size={12} /> {busyCap ? '…' : 'Rewrite'}</button>
            <button onClick={() => onSave(post, caption)} disabled={!dirty} style={{ ...miniBtn, opacity: dirty ? 1 : 0.5 }}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── stepper ── */
function Stepper({ steps, currentIdx }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <div style={{ width: 22, height: 22, borderRadius: '50%', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: i < currentIdx ? '#dcfce7' : i === currentIdx ? '#4f46e5' : '#f3f4f6',
              color: i < currentIdx ? '#16a34a' : i === currentIdx ? '#fff' : '#9ca3af' }}>
              {i < currentIdx ? '✓' : i + 1}
            </div>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: i === currentIdx ? '#4f46e5' : '#9ca3af' }}>{s.label}</span>
          </div>
          {i < steps.length - 1 && <div style={{ width: 20, height: 1, background: '#e5e7eb' }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ── shared bits ── */
const SectionTitle = ({ children }) => <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 12 }}>{children}</div>;
const Label = ({ children }) => <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{children}</div>;
const Footer = ({ left, right }) => <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}><div>{left}</div><div>{right}</div></div>;
const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, color: '#111827', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' };
const primary = (disabled) => ({ padding: '10px 18px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 });
const ghost = { padding: '9px 14px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const miniBtn = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const pill = (active) => ({ padding: '7px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `1px solid ${active ? '#4f46e5' : '#d1d5db'}`, background: active ? '#eef2ff' : '#fff', color: active ? '#4f46e5' : '#374151' });
const checkRow = { display: 'flex', alignItems: 'center', gap: 7, fontSize: 13.5, color: '#374151', cursor: 'pointer' };
const refThumb = { width: 56, height: 56, borderRadius: 8, border: '2px solid #e5e7eb', background: '#f9fafb', padding: 0, cursor: 'pointer', overflow: 'hidden' };
const refThumbActive = { borderColor: '#4f46e5', boxShadow: '0 0 0 2px rgba(79,70,229,0.2)' };
